import React, { useCallback, useEffect } from 'react'

import { useTranslation } from 'language/translator'
import { useWidgetLoadingTitles } from 'utils/loadingTitles'
import { parseQueryParamToNumber } from 'utils/queryHandler'
import { TCoreWidget } from 'types/GlobalWidget'

import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'

import Box from 'components_new/atoms/Box'

import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'
import LoadingSection from 'components_new/molecules/LoadingSection'

import Dashboard from 'components_new/organisms/Dashboard'

import PublicDashboardTemplate from 'components_new/templates/PublicDashboardTemplate'
import { DashboardVariantType, isPublicDashboard } from 'types/GlobalDashboard'

import { useAutoRefresh, useDocumentTitle, useParams } from 'utils/hooks'
import { useLocation } from 'react-router-dom'
import { useDashboard, useDashboardFetchStatus } from 'redux/hooks/Dashboards'
import { useDashboardActions } from 'redux/actions/Dashboards'

const PublicDashboardPage = () => {
  const loadingTitles = useWidgetLoadingTitles()
  const { t } = useTranslation()

  const params = useParams<{ id: string }>()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)

  const mobileBreakpoint = parseQueryParamToNumber(
    queryParams.get('mobile_breakpoint')
  )
  const mobileResponsive = queryParams.get('responsive_mode') === 'mobile'

  useAutoRefresh()

  const dashboard = useDashboard(params.id)
  const dashboardFetchStatus = useDashboardFetchStatus(params.id)

  useDocumentTitle(dashboard?.title)

  const { tryGetOnePublicDashboardWidget } = useDashboardWidgetActions()
  const { tryGetOnePublicDashboard } = useDashboardActions()

  // Pre-configured fetch function for individual widgets
  const fetchWidgetData = useCallback(
    (widget: TCoreWidget) => {
      tryGetOnePublicDashboardWidget(widget, params.id, location.search)
    },
    [params.id, location.search]
  )

  useEffect(() => {
    tryGetOnePublicDashboard(params.id)
  }, [])

  if (!dashboard || !isPublicDashboard(dashboard)) {
    // check for loading or error
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          width: '100vw',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {dashboardFetchStatus.status === 'error' ? (
          <ErrorPlaceholder
            code={401}
            body={t('dashboards', 'error_public_dashboard_body')}
          />
        ) : (
          <LoadingSection description={loadingTitles} />
        )}
      </Box>
    )
  }

  return (
    <PublicDashboardTemplate
      dashboard={dashboard}
      mobileBreakpoint={mobileBreakpoint}
      mobileResponsive={mobileResponsive}
    >
      <Dashboard
        customization={dashboard.white_label_settings}
        dashboard={dashboard}
        dashboardFilters={dashboard.dashboard_filters}
        fetchWidgetData={fetchWidgetData}
        variant={{
          embedded: true,
          type: DashboardVariantType.PUBLIC
        }}
      />
    </PublicDashboardTemplate>
  )
}

export default PublicDashboardPage
